/******************************************************************************************
 *	SSP List
 *******************************************************************************************
 *
 * SspList is short for `searchable, sortable, paginated list`.
 * This service provides pagination, sorting and searching for arrays of data.
 *
 * Constructor Params:
 * data: is the data source upon which to apply filtering, sorting and pagination
 * options: is a config object. Available directives are as shown below:
 * options.searchFields: an array of strings or objects that represent fields to search in.
 * options.itemsPerPage: number representing the maximum number of items a page should have
 * options.currentPage: starting page, by default this is 1
 * options.minSearchLength: integer representing minimum char lenght for search queries and sub-queries, by default this is 3
 * options.caseInsensitiveSort: default true if string sorting should be case-insensitive and false otherwise
 *
 * get() method returns the data modified as requested via the options config argument along with configuration details (pagination current and total pages, sorting column and direction and search query applied)
 * These configurations are stacked unless cleared. Make 2 calls to get(), one with sorting and another with pagination
 * will return a data set sorted and paginated.
 *
 * get(options) Params
 * options.query: search query to match data in searchFields
 * options.sortBy: string or array representing column (property) names to sort by
 * options.sortDirection: string 'desc' or 'asc', defaults to 'asc', represents sorting direction
 * options.sortNumeric: boolean, if true, sorting will be done numerically instead of lexicographically
 * options.page: number representing the page to be returned
 *
 * changeDataset() method receives a new dataset to replace the inital one, re-applying search and sort. It does not touch anything else (e.g. fallbacks, searchFields etc).
 *
 * getOriginalDataset() method returns an unaltered copy of the original dataset.
 *
 */
(function() {


  angular.module('rcServices.sspList', []).service(
    'SspList',
    /* @ngInject */
    function(_, Settings, Utility) {
      function SspList(data, options) {
        //Make sure data and options are at the very least empty objects
        data = data || [];
        options = options || {};

        //Make a copy of the original data object
        var originalData = data.slice();

        //Copy of the passed in data on which to apply filters and sorting
        var workData = data.slice();

        //Number of max items per page
        var itemsPerPage = options.itemsPerPage || Settings.itemsPerPage.default;

        //The fields in which search will look for matches
        //It's an array of fields (propery names) to search in
        var searchFields = options.searchFields || [];
        var searchFieldsCount = searchFields.length;

        //Set min search length
        var minSearchLength = options.minSearchLength || -1;

        //Sorting case sensitivity
        var caseInsensitiveSort = options.caseInsensitiveSort || true;

        //If sorting based on options.sortBy passed to .get() method produces columns with identical values, fallback sorting criteria will be applied.
        var fallbackSortColumn;
        var fallbackSortDirection;
        var fallbackSortIsNumeric;
        if (options.fallbackSort) {
          fallbackSortColumn = options.fallbackSort.column || null;
          fallbackSortDirection = options.fallbackSort.direction === 'desc' ? -1 : 1;
          fallbackSortIsNumeric = options.fallbackSort.isNumeric || false;
        }

        //State vars
        var currentPage = options.currentPage || 1;
        var currentSortColumn = null;
        var currentSortDirection = 1; //1 means asc, -1 means desc
        var currentSortIsNumeric = false;
        var currentSearchQuery = null;

        //Computes and returns the number of pages for a given number of items
        var computePageTotal = function(totalItems) {
          return Math.ceil(totalItems / itemsPerPage);
        };

        //Returns a string representing the search haystack made up of all data specified by searchFields inside of item argument
        var getHaystack = function(item) {
          var haystack = [];
          for (var i = 0; i < searchFieldsCount; i++) {
            haystack.push(Utility.getValueFromPropertySequence(item, searchFields[i]) || '');
          }
          return haystack.join('');
        };

        //Search function
        var filterFunction = function(item) {
          if (currentSearchQuery) {
            //Create a string from all searchFields to search in
            var haystack = _.toLower(getHaystack(item));

            //Compute total number of needles
            //Make the search more heuristic by searching not only by the original search
            //but also by valid substrings of the original search string
            var needles = _(currentSearchQuery)
              .split(' ')
              .filter(function(item) {
                return _.trim(item).length >= minSearchLength;
              })
              .value();

            //Inject the original search string at the top of the list
            //Length is 1 when currentSearchQuery is only one word so no need to inject the same needle twice.
            if (needles.length !== 1) {
              needles.unshift(currentSearchQuery);
            }

            //Search for needles in haystack
            var hasMatch = _.some(needles, function(needle) {
              if (_.includes(haystack, needle)) {
                return true;
              }
            });

            return hasMatch;
          }
          return true;
        };

        //Returns the values to be compared for items `a` and `b`
        var getValuesToCompare = function(a, b, sortBy, numericSort) {
          var flatA = [];
          var flatB = [];

          //If multiple sort column names were specified, split and flatten each
          var sortColumns = _.split(sortBy, ',');
          var sortColumnsCount = sortColumns.length;

          //Loop each sort column (object property) and get its value
          for (var i = 0; i < sortColumnsCount; i++) {
            flatA.push(Utility.getValueFromPropertySequence(a, sortColumns[i]) || '');
            flatB.push(Utility.getValueFromPropertySequence(b, sortColumns[i]) || '');
          }

          //Flatten the array for comparison
          flatA = flatA.join('');
          flatB = flatB.join('');

          //If current sort should be numeric, convert both results to numbers
          if (numericSort) {
            //If the conversion fails (NaN), value will be -Infinity so that it is last
            flatA = Number(flatA) || -Infinity;
            flatB = Number(flatB) || -Infinity;
          } else if (caseInsensitiveSort) {
            flatA = _.toLower(flatA);
            flatB = _.toLower(flatB);
          }

          return {
            flatA: flatA,
            flatB: flatB
          };
        };
        var sortFunction = function(a, b) {
          var values = getValuesToCompare(a, b, currentSortColumn, currentSortIsNumeric);
          var flatA = values.flatA;
          var flatB = values.flatB;

          if (flatA > flatB) {
            return 1 * currentSortDirection;
          }
          if (flatA < flatB) {
            return -1 * currentSortDirection;
          } else if (fallbackSortColumn && fallbackSortColumn !== currentSortColumn) {
            //If flatA and flatB are the same, see if you can better sort using the fallbackSortColumn
            values = getValuesToCompare(a, b, fallbackSortColumn, fallbackSortIsNumeric);
            flatA = values.flatA;
            flatB = values.flatB;

            if (flatA > flatB) {
              return 1 * fallbackSortDirection;
            }
            if (flatA < flatB) {
              return -1 * fallbackSortDirection;
            }
          }

          // If still equal, sort on id field so that we get a stable sort.
          var aId = a.id || '';
          var bId = b.id || '';
          if (aId > bId) {
            return currentSortDirection; // 1 * currentSortDirection
          } else if (aId < bId) {
            return -currentSortDirection; // -1 * currentSortDirection
          }

          return 0;
        };

        //Helper functions that apply current criteria for search, sort or pagination.
        var applySearch = function() {
          if (currentSearchQuery) {
            //Always filter originalData, otherwise you would be applying filter over filter
            workData = _.filter(originalData, filterFunction);
          }
        };
        var applySort = function() {
          if (currentSortColumn) {
            workData.sort(sortFunction);
          }
        };

        //Returns workData or a subset of it, modified as specified in the options parameter
        //Params previously passed-in will persist until reset:
        //For example, if get({page:2}) is called first, then get({sortBy:'name'}), this second call will
        //return the second page after sorting was applied, no need to explicitly set page to 2 again.
        this.get = function(options) {
          options = options || {};

          /*=== Search ===*/
          var searchQuery = options.query;

          //Because searching creates a new dataset, sorting will be wrong unless redone
          //When searchApplied is true, if there is a sort in place, it will be applied to the new dataset
          //This is ignored if the options config already contains a sortBy property
          //For example, for {query:'Rooney',sortBy:'goals'}, if the previous sort was something else, sorting will be done by the value of data.goals
          var searchApplied = false;

          //Perform search if searchQuery is set, if it's not the same as the current one (in which case the data set is already sorted) and if it's valid (min char constraint)
          if (
            searchQuery &&
            searchQuery !== currentSearchQuery &&
            _.trim(searchQuery).length >= minSearchLength
          ) {
            currentSearchQuery = _.toLower(searchQuery);

            applySearch();

            //Make sure the new dataset is sorted
            searchApplied = true;
          }

          /*=== Sorting ==*/
          var sort = options.sortBy;

          if (sort) {
            //If a specific sort direction was requested, set current sort direction to match it
            if (options.sortDirection) {
              currentSortDirection = options.sortDirection === 'desc' ? -1 : 1;
            } else {
              //Otherwise, imply the sort direction
              //Same sort column, toggle sort direction
              if (sort === currentSortColumn) {
                currentSortDirection = currentSortDirection === 1 ? -1 : 1;
              } else {
                //New sort column, default to asc direction
                currentSortDirection = 1;
              }
            }

            //Check if the requested sort should be numeric or lexicographic
            currentSortIsNumeric = options.sortNumeric === true;

            currentSortColumn = sort;
            applySort();
          } else if (searchApplied && currentSortColumn) {
            applySort();
          }

          /*=== Pagination ===*/
          //Compute total pages
          var page;
          var totalPages = computePageTotal(workData.length);
          if (sort || searchQuery) {
            //Sorting and searching resets pagination unless explicitly set as an option on the same call
            page = options.page || 1;
          } else {
            //Otherwise, default to currentPage if not set in options
            page = options.page || currentPage;
          }

          //Make sure page is within bounds
          if (page < 1) {
            page = 1;
          } else if (page > totalPages) {
            page = totalPages;
          }

          currentPage = page;

          //Paginate output
          var output = workData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

          return {
            pagination: {
              currentPage: currentPage,
              totalPages: totalPages
            },
            sort: {
              column: currentSortColumn,
              direction: currentSortDirection === -1 ? 'desc' : 'asc',
              numeric: currentSortIsNumeric
            },
            search: {
              query: currentSearchQuery
            },
            data: output
          };
        };

        //Clears the current search
        this.clearSearch = function() {
          //Reset paging
          currentPage = 1;

          //Remove currentSearchQuery
          currentSearchQuery = null;

          //Restore workData (searching removes items, so clearing search must restore them)
          workData = originalData.slice();

          //If sorting existed before clearing search, re-apply sorting to new dataset
          if (currentSortColumn) {
            applySort();
          }
        };

        //Changes the dataset without removing any current criteria (search, sort or paging)
        this.changeDataset = function(data) {
          //Re-set original data
          originalData = data.slice();
          //Re-set work data
          workData = data.slice();
          //Re-apply search and sort. Only works if current criteria for search and sort are present.
          applySearch();
          applySort();
        };

        //Returns an unaltered copy of the original data set, unaffected by filters or sorting
        this.getOriginalDataset = function() {
          return originalData;
        };

        //Allows changing minSearchLength after instatiation
        this.updateMinSearchLength = function(limit) {
          minSearchLength = parseInt(limit, 10) || 1;
        };
      }

      return SspList;
    }
  );
})();
